import React, { FC } from "react";
import styled from "styled-components";
import { Button } from "@pepdirect/ui/button";
import { Divider } from "../divider/Divider";
import { Heading } from "@pepdirect/ui/heading";
import { Cart, CartItem } from "@pepdirect/shared/types";
import { formatDollars } from "../../helpers/currency";
import { Device } from "../..//helpers/SharedHelpers/screenSizes";

const StyledOrderSummary = styled.div`
  width: 100%;
  border-radius: 3px;
  padding: 20px;
  border: 1px solid white;

  @media ${Device.mobile} {
    border: none;
    border-radius: 0;
    padding: 10px 0 0 0;
  }
`;

const HeadingWrapper = styled.div`
  @media ${Device.mobile} {
    display: none;
  }
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 10px;
  color: white;
  @media ${Device.mobile} {
    display: none;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  text-transform: uppercase;
  font-size: 16px;

  &:first-of-type {
    @media ${Device.mobile} {
      padding-top: 20px;
    }
  }
`;

const StyledDivider = styled(Divider)`
  margin: 10px 0;
`;

const BoldText = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 20px;
  font-family: "AktivGrotesk Ex Black Italic", sans-serif;
`;

const ThemedText = styled.div`
  color: ${({ theme }) => theme.priceAccentColor || theme.color.action};
  font-weight: 500;
`;

const ButtonWrapper = styled.div`
  padding-top: 10px;

  @media ${Device.mobile} {
    padding-top: 15px;
  }
`;

interface OrderSummaryProps {
  cart: Cart | null;
  subtotal: number;
  total: number;
  freeShipping: boolean;
  isButtonDisabled: boolean;
  goToCheckout: () => void;
}

export const OrderSummary: FC<OrderSummaryProps> = ({
  cart,
  subtotal,
  total,
  freeShipping,
  isButtonDisabled,
  goToCheckout,
}) => {
  const getTotalSavings = () => {
    if (!cart?.items?.length) return 0;

    return (
      cart.items.reduce((savings: number, item: CartItem) => {
        if (item.product.compareAtPrice && item.product.price) {
          return (
            savings +
            (item.product.compareAtPrice - item.product.price) * item.quantity
          );
        } else {
          return savings;
        }
      }, 0) / 100
    );
  };

  const savings = getTotalSavings();

  return (
    <StyledOrderSummary>
      <HeadingWrapper>
        <StyledHeading level="h3" size="m">
          Order Summary
        </StyledHeading>

        <StyledDivider />
      </HeadingWrapper>

      <SummaryItem>
        <div>Subtotal</div>
        <div>{formatDollars(subtotal)}</div>
      </SummaryItem>

      <SummaryItem>
        <div>Shipping calculated at checkout</div>
      </SummaryItem>

      <StyledDivider />

      <SummaryItem>
        <BoldText>Grand Total</BoldText>
        <BoldText>{formatDollars(total)}</BoldText>
      </SummaryItem>

      {savings > 0 && (
        <SummaryItem>
          <span></span>
          <ThemedText>You Save {formatDollars(savings)}</ThemedText>
        </SummaryItem>
      )}

      <ButtonWrapper>
        <Button
          data-testid="proceed-checkout-button"
          type="secondary"
          fullWidth
          onClick={goToCheckout}
          disabled={isButtonDisabled}
          style={{
            color: "black",
            background: "#CF9C43",
            fontFamily: "AktivGrotesk Ex Black Italic",
            textTransform: "uppercase",
            borderRadius: 0,
            border: "none",
          }}
        >
          Proceed To Checkout
        </Button>
      </ButtonWrapper>
    </StyledOrderSummary>
  );
};
